import { MovieClip } from '@play-co/timestep-core/ui';
import { Container } from '../../../odie';
import { getMatch2AssetPath } from '../../util/assets';
import { blockPositionView } from '../../util/blockTools';
import {
  BalloonBoxBlockProps,
  BalloonBoxState,
  BalloonBoxStateFlag,
} from './BalloonBoxBlock';
import { playAnimationSequence } from '../../util/animation';

const manifest = () => ({
  movieclip: {
    url: getMatch2AssetPath('blocks/balloonBox/movieclip'),
    loadMethod: MovieClip.animationLoader,
  },
  all: getMatch2AssetPath('blocks/balloonBox'),
});

const animationMap = {
  base: {
    idle: 'balloonbox_stage1',
    fill: [],
    explode: 'balloonbox_stage2',
  },
  blueA: {
    idle: 'balloon_blue_A_stage1',
    fill: ['balloon_blue_A_stage2', 'balloon_blue_A_stage3'],
    explode: 'balloon_blue_A_stage4',
  },
  blueB: {
    idle: 'balloon_blue_B_stage1',
    fill: ['balloon_blue_B_stage2', 'balloon_blue_B_stage3'],
    explode: 'balloon_blue_B_stage4',
  },
  greenA: {
    idle: 'balloon_green_A_stage1',
    fill: ['balloon_green_A_stage2', 'balloon_green_A_stage3'],
    explode: 'balloon_green_A_stage4',
  },
  greenB: {
    idle: 'balloon_green_B_stage1',
    fill: ['balloon_green_B_stage2', 'balloon_green_B_stage3'],
    explode: 'balloon_green_B_stage4',
  },
  redA: {
    idle: 'balloon_red_A_stage1',
    fill: ['balloon_red_A_stage2', 'balloon_red_A_stage3'],
    explode: 'balloon_red_A_stage4',
  },
  redB: {
    idle: 'balloon_red_B_stage1',
    fill: ['balloon_red_B_stage2', 'balloon_red_B_stage3'],
    explode: 'balloon_red_B_stage4',
  },
  yellowA: {
    idle: 'balloon_yellow_A_stage1',
    fill: ['balloon_yellow_A_stage2', 'balloon_yellow_A_stage3'],
    explode: 'balloon_yellow_A_stage4',
  },
  yellowB: {
    idle: 'balloon_yellow_B_stage1',
    fill: ['balloon_yellow_B_stage2', 'balloon_yellow_B_stage3'],
    explode: 'balloon_yellow_B_stage4',
  },
};

type Layer = keyof typeof animationMap;
type Anim = 'idle' | 'fill' | 'explode';

export class BalloonBoxBlockView extends Container {
  private layers: Partial<Record<Layer, MovieClip>> = {};

  constructor(props: BalloonBoxBlockProps) {
    super();
    blockPositionView(this, props);
    // Set up initial layers
    this.playAnimation('base', 'idle');
    this.playAnimation('greenA', 'idle');
    this.playAnimation('yellowA', 'idle');
    this.playAnimation('blueA', 'idle');
    this.playAnimation('redA', 'idle');
    this.playAnimation('yellowB', 'idle');
    this.playAnimation('blueB', 'idle');
    this.playAnimation('redB', 'idle');
    this.playAnimation('greenB', 'idle');
  }

  public static assets() {
    return Object.values(manifest());
  }

  public updateBalloons(state: BalloonBoxState) {
    for (const key in state) {
      const fill = state[key as BalloonBoxStateFlag];
      const zIndex = fill ? 1 : 0;
      const movieclip = this.getMovieclip(key as Layer);
      if (!fill || movieclip.style.zIndex) continue;
      this.playAnimation(key as Layer, 'fill');
      movieclip.style.zIndex = zIndex;
    }
  }

  public async explode() {
    const promises = Object.keys(this.layers).map((key) =>
      this.playAnimation(key as Layer, 'explode'),
    );
    await Promise.all(promises);
  }

  private getMovieclip(name: Layer) {
    let movieclip = this.layers[name];
    if (!movieclip) {
      movieclip = new MovieClip({
        superview: this.view,
        url: manifest().movieclip.url,
        scale: 0.97,
        zIndex: 0,
      });
      this.layers[name] = movieclip;
    }
    return movieclip;
  }

  private async playAnimation(layer: Layer, anim: Anim) {
    const movieclip = this.getMovieclip(layer);
    const config = animationMap[layer];
    const isList = Array.isArray(config[anim]);
    const animationList = (isList ? config[anim] : [config[anim]]) as string[];
    await playAnimationSequence({
      movieclip: movieclip,
      animations: animationList,
      loopLast: isList,
    });
  }
}

export function _createBalloonBoxBlockView(
  props: BalloonBoxBlockProps,
  option: number,
): BalloonBoxBlockView {
  const view = new BalloonBoxBlockView(props);
  return view;
}
